import type { BracketResult } from "../types/tax.types";

const BRACKET_COLORS = [
  "#E8E6E1", // 0%
  "#FAC775", // 5%
  "#EF9F27", // 10%
  "#E8A020", // 15%
  "#D4881A", // 20%
  "#BA7517", // 25%
  "#9A5F0E", // 30%
  "#7A4A08", // 35%
];

function formatBaht(n: number) {
  if (n >= 1_000_000) return `฿${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `฿${(n / 1_000).toFixed(0)}K`;
  return `฿${n}`;
}

function formatRate(rate: number) {
  return `${(rate * 100).toFixed(0)}%`;
}

export default function BracketChart({
  brackets,
}: {
  brackets: BracketResult[];
}) {
  const activeBrackets = brackets.filter((b) => b.taxableInBracket > 0);
  const maxTaxable = Math.max(
    ...activeBrackets.map((b) => b.taxableInBracket),
    1,
  );

  if (activeBrackets.length === 0) {
    return (
      <div className="text-center py-6 text-[13px] text-muted-foreground">
        Enter income to see calculations
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {activeBrackets.map((bracket, i) => {
        const colorIndex = brackets.indexOf(bracket);
        const barWidth = (bracket.taxableInBracket / maxTaxable) * 100;
        const color = BRACKET_COLORS[colorIndex] ?? BRACKET_COLORS[7];
        const isFirst = bracket.rate === 0;

        return (
          <div key={i} className="group">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[11px] text-muted-foreground w-7 text-right font-sans [font-variant-numeric:tabular-nums]">
                {formatRate(bracket.rate)}
              </span>
              <div className="flex-1 h-5 bg-muted rounded-sm overflow-hidden">
                <div
                  className="h-full rounded-sm transition-all duration-300"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: color,
                    opacity: isFirst ? 0.4 : 1,
                  }}
                />
              </div>
              <span className="text-[11px] text-muted-foreground w-14 text-right font-sans [font-variant-numeric:tabular-nums]">
                {isFirst ? "—" : `฿${bracket.taxInBracket.toLocaleString()}`}
              </span>
            </div>
            <div className="pl-9 text-[10px] text-muted-foreground/70">
              {formatBaht(bracket.from)} –{" "}
              {bracket.to ? formatBaht(bracket.to) : "∞"} ·{" "}
              {formatBaht(bracket.taxableInBracket)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
